/**
 * The marks at the top of a job page (The Quiet Toolbar, 10/10): the same
 * state the Grid card shows (lib/job-marks.ts), so a red flag on the card is
 * a red flag here too, and doing the work clears both. Renders nothing when
 * the switch is off or there is nothing to say. The red "Tech flagged" mark
 * carries a ✓ Handled button for jobs.manage (2026-10-29): it used to stay
 * red until the whole job closed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mark } from "@/components/mark";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { isTechFlagMark, jobMarks, type JobMarkFacts } from "@/lib/job-marks";
import { useQuietToolbar } from "@/lib/quiet-toolbar";

export function JobMarksStrip({
    job,
    canHandleFlag = false,
}: {
    job: JobMarkFacts & { id?: string; cannot_complete_reason?: string | null };
    /** jobs.manage: may close the tech's flag (POST /jobs/:id/tech-flags/handled). */
    canHandleFlag?: boolean;
}) {
    const [quiet] = useQuietToolbar();
    const qc = useQueryClient();
    const handled = useMutation({
        mutationFn: () => api.post(`/api/jobs/${job.id}/tech-flags/handled`),
        onSuccess: () => {
            toast.success("Marked handled. The red flag is gone.");
            void qc.invalidateQueries({ queryKey: ["job", job.id] });
            void qc.invalidateQueries({ queryKey: ["jobs"] });
            void qc.invalidateQueries({ queryKey: ["dispatch"] });
        },
        onError: () => toast.error("Couldn't mark it handled. Try again."),
    });
    if (!quiet) return null;
    // "No work order yet" has its own red banner with the Upload button
    // (no-work-order-banner.tsx) — never say it twice on one screen.
    const marks = jobMarks(job)
        .filter((m) => m.label !== "No work order yet")
        .map((m) =>
            m.label === "Can't finish" && job.cannot_complete_reason
                ? { ...m, label: `Can't finish: ${job.cannot_complete_reason}` }
                : m,
        );
    if (marks.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5 rounded-xl border bg-card px-3 py-2" data-testid="job-marks-strip">
            {marks.map((m) =>
                isTechFlagMark(m.label) && canHandleFlag && job.id ? (
                    <span key={m.label} className="inline-flex items-center gap-1.5">
                        <Mark kind={m.kind} label={m.label} title={m.title} className="text-[13px]" />
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            disabled={handled.isPending}
                            onClick={() => handled.mutate()}
                            data-testid="tech-flag-handled"
                            title="You've dealt with what the tech flagged. This clears the red flag."
                        >
                            ✓ Handled
                        </Button>
                    </span>
                ) : (
                    <Mark key={m.label} kind={m.kind} label={m.label} title={m.title} className="text-[13px]" />
                ),
            )}
        </div>
    );
}
